import api from './api'

const authService = {
  /**
   * Register a new account
   */
  register: async (data) => {
    const response = await api.post('/auth/register', data)
    return response.data
  },

  /**
   * Login with email and password
   */
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password })
    const { access_token, refresh_token, user } = response.data

    // Store tokens
    localStorage.setItem('accessToken', access_token)
    localStorage.setItem('refreshToken', refresh_token)

    return user
  },

  /**
   * Logout - clear tokens
   */
  logout: () => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
  },

  /**
   * Get current user information
   */
  getCurrentUser: async () => {
    const response = await api.get('/accounts/me')
    return response.data
  },

  /**
   * Google OAuth login
   */
  googleLogin: async (credential) => {
    const response = await api.post('/auth/oauth/google', { credential })
    const { access_token, refresh_token, user } = response.data

    localStorage.setItem('accessToken', access_token)
    localStorage.setItem('refreshToken', refresh_token)

    return user
  },

  /**
   * Facebook OAuth login
   */
  facebookLogin: async (accessToken) => {
    const response = await api.post('/auth/oauth/facebook', { access_token: accessToken })
    const { access_token, refresh_token, user } = response.data

    localStorage.setItem('accessToken', access_token)
    localStorage.setItem('refreshToken', refresh_token)

    return user
  },

  /**
   * Twitter OAuth login
   */
  twitterLogin: async (code) => {
    const response = await api.post('/auth/oauth/twitter', { code })
    const { access_token, refresh_token, user } = response.data

    localStorage.setItem('accessToken', access_token)
    localStorage.setItem('refreshToken', refresh_token)

    return user
  },

  /**
   * Send phone verification code (Signal-style auth)
   */
  sendPhoneCode: async (phoneNumber) => {
    const response = await api.post('/auth/phone/send-code', { phone_number: phoneNumber })
    return response.data
  },

  /**
   * Verify phone code and login/register (Signal-style auth)
   */
  verifyPhoneCode: async (phoneNumber, code, name = null) => {
    const payload = { phone_number: phoneNumber, code }
    if (name) {
      payload.name = name
    }
    const response = await api.post('/auth/phone/verify', payload)
    const { access_token, refresh_token, user, is_new_user } = response.data

    localStorage.setItem('accessToken', access_token)
    localStorage.setItem('refreshToken', refresh_token)

    return { user, isNewUser: is_new_user }
  },

  /**
   * Signal-style login (alias for verifyPhoneCode)
   */
  signalLogin: async (phoneNumber, code, name = null) => {
    return authService.verifyPhoneCode(phoneNumber, code, name)
  },

  /**
   * Activate account with token
   */
  activateAccount: async (token) => {
    const response = await api.post(`/auth/activate/${token}`)
    return response.data
  },

  /**
   * Request password reset
   */
  requestPasswordReset: async (email) => {
    const response = await api.post('/auth/password/reset', { email })
    return response.data
  },

  /**
   * Reset password with token
   */
  resetPassword: async (token, newPassword) => {
    const response = await api.put('/auth/password/reset', { token, new_password: newPassword })
    return response.data
  },

  /**
   * Change password (authenticated)
   */
  changePassword: async (currentPassword, newPassword) => {
    const response = await api.put('/auth/password/change', {
      current_password: currentPassword,
      new_password: newPassword,
    })
    return response.data
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('accessToken')
  },
}

export default authService
